import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
export function HeroSection() {
  return <section className="relative w-full pt-12 pb-20 px-6 md:pt-20 md:pb-24 overflow-hidden bg-warm-white" data-id="element-76">
      <div className="max-w-4xl mx-auto flex flex-col items-center" data-id="element-77">
        {/* Label */}
        <motion.span initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="inline-block px-3 py-1 mb-6 text-xs font-bold tracking-widest text-teal uppercase bg-teal/10 rounded-full" data-id="element-78">
          For Moms of Sick Kids Under 5
        </motion.span>

        {/* Headline */}
        <motion.h1 initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="text-4xl md:text-5xl lg:text-6xl font-bold text-center text-navy leading-[1.1] mb-6 max-w-3xl" data-id="element-79">
          Stop Walking Into the ER With a Blank Mind.
        </motion.h1>

        {/* Subheadline */}
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="text-lg md:text-xl text-muted text-center mb-12 max-w-2xl font-medium" data-id="element-80">
          The one-page sheet that makes doctors take you seriously.
        </motion.p>

        {/* Product Image */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="relative w-full max-w-5xl mb-16 px-0" data-id="element-81">
          <img src="https://cdn.magicpatterns.com/uploads/qB764RbSJCtEJ7KvieGSxS/heylloyd-image-1771105725003.png" alt="The ER-Ready Mom Binder on a tablet in a hospital setting" className="w-full h-auto object-cover rounded-2xl drop-shadow-[0_0px_80px_rgba(197,221,216,0.7)]" data-id="element-82" />
        </motion.div>

        {/* Opening Lead - Letter Style */}
        <div className="max-w-reading w-full space-y-6 text-lg md:text-xl leading-relaxed text-body" data-id="element-83">
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-84">
            You know that moment.
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} data-id="element-85">
            Your child isn't okay. You're trying to stay calm. And your brain
            suddenly feels like it's full of static.
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} data-id="element-86">
            They ask questions fast—dates, meds, reactions, what changed, what
            "normal" looks like for your child. And you can feel yourself
            reaching for information you know you know… but it won't come.
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="font-semibold text-navy italic" data-id="element-87">
            Not because you don't care. Because you're carrying too much.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} className="bg-white p-6 md:p-8 rounded-lg border-l-4 border-teal shadow-sm my-8" data-id="element-88">
            <p className="mb-4 font-medium text-navy" data-id="element-89">
              The ER‑Ready Mom Binder is a printable, grab‑and‑go system built
              for the exact moments when your memory stops cooperating.
            </p>
            <ul className="space-y-3" data-id="element-90">
              <li className="flex items-start gap-3" data-id="element-91">
                <CheckCircle2 className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-92" />
                <span data-id="element-93">One page that runs the room (the Medical Snapshot)</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-94">
                <CheckCircle2 className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-95" />
                <span data-id="element-96">
                  A 15-minute weekly loop so it stays updated without becoming
                  your new full-time job
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-97">
                <CheckCircle2 className="w-5 h-5 text-teal flex-shrink-0 mt-1" data-id="element-98" />
                <span data-id="element-99">
                  A simple way to leave every visit with a plan, not a blur
                </span>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.5
      }} className="flex flex-col items-center mt-8" data-id="element-100">
          <a href="#offer" onClick={e => {
          e.preventDefault();
          document.getElementById('offer')?.scrollIntoView({
            behavior: 'smooth'
          });
        }} className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white bg-navy rounded-full overflow-hidden transition-transform hover:scale-[1.02] shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy" data-id="element-101">
            <span data-id="element-102">
              Get the Binder —{' '}
              <span className="line-through opacity-60 mr-1" data-id="element-103">$17</span> $6.36
            </span>
            <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-104" />
          </a>
          <p className="mt-3 text-sm text-muted" data-id="element-105">
            Instant PDF download. Set up in 24 hours.{' '}
            <span className="text-teal font-semibold" data-id="element-106">You save $10.64</span>
          </p>
        </motion.div>
      </div>
    </section>;
}